import { Box, Flex, SimpleGrid, Text, useToast } from '@chakra-ui/react';
import PropTypes from 'prop-types';
import DownloadExcelButton from './DownloadExcelButton';
import { downloadClientCohortSlides } from './calc/exportClientCohortSlides';
import {
  buildClientCohortView,
  buildClientCohortViewFromDealIrrModel,
  computeClientCohortDisplayEquity,
  computeClientCohortDisplayTotals,
  getClientCohortDisplayColumns,
  selectClientCohortDisplayValues,
} from './calc/buildClientCohortView';
import { fmtMoney } from './calc/formatters';

function fmtThousands(value) {
  const amount = Number(value) || 0;
  const thousands = Math.round(amount / 1000);
  if (thousands === 0) {
    return '—';
  }
  const formatted = Math.abs(thousands).toLocaleString('en-US');
  return amount < 0 ? `(${formatted})` : formatted;
}

function fmtRoasPct(value) {
  const pct = Number(value) || 0;
  if (pct === 0) {
    return '—';
  }
  return `${Math.round(pct)}%`;
}

function fmtMultiple(value) {
  if (value == null || !Number.isFinite(value)) {
    return '—';
  }
  return `${value.toFixed(1)}x`;
}

function fmtCappedReturnPct(value) {
  if (value == null || !Number.isFinite(value)) {
    return '—';
  }
  return `${value.toFixed(2)}% / mo`;
}

function ClientTableCell({ value, format = 'money', highlight = false }) {
  const isNegative = Number(value) < 0;
  const isPositive = Number(value) > 0;
  let displayValue = '—';

  if (format === 'roas') {
    displayValue = fmtRoasPct(value);
  } else if (format === 'money') {
    displayValue = fmtThousands(value);
  }

  const className = [
    'client-cohort-cell',
    highlight ? 'client-cohort-cell-highlight' : '',
    format === 'money' && isNegative ? 'client-cohort-negative' : '',
    format === 'money' && isPositive ? 'client-cohort-positive' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return <td className={className}>{displayValue}</td>;
}

function ClientTableRow({
  label,
  values,
  total,
  format = 'money',
  highlight = false,
  bold = false,
}) {
  return (
    <tr className={highlight ? 'client-cohort-highlight-row' : ''}>
      <td className={`client-cohort-label ${bold ? 'client-cohort-label-bold' : ''}`}>
        {label}
      </td>
      {values.map((value, index) => (
        <ClientTableCell
          key={`${label}-${index}`}
          value={value}
          format={format}
          highlight={highlight}
        />
      ))}
      <ClientTableCell value={total} format={format} highlight={highlight} />
    </tr>
  );
}

function TopKpiCard({ label, value, sublabel }) {
  return (
    <Box className="client-cohort-top-kpi">
      <Text className="client-cohort-top-kpi-label">{label}</Text>
      <Text className="client-cohort-top-kpi-value">{value}</Text>
      {sublabel ? (
        <Text className="client-cohort-top-kpi-sublabel">{sublabel}</Text>
      ) : null}
    </Box>
  );
}

function BottomKpiCard({ label, value, sublabel, accent = false }) {
  return (
    <Box className={`client-cohort-bottom-kpi ${accent ? 'client-cohort-bottom-kpi-accent' : ''}`}>
      <Text className="client-cohort-bottom-kpi-label">{label}</Text>
      <Text className="client-cohort-bottom-kpi-value">{value}</Text>
      {sublabel ? (
        <Text className="client-cohort-bottom-kpi-sublabel">{sublabel}</Text>
      ) : null}
    </Box>
  );
}

function ClientCohortSection({ calcResult, dealIrrModel, dealIrrInputs, state }) {
  const toast = useToast();
  const view = dealIrrModel
    ? buildClientCohortViewFromDealIrrModel(dealIrrModel, state, dealIrrInputs)
    : buildClientCohortView(calcResult, state);
  if (!view) {
    return null;
  }

  const fundingPctLabel = Math.round(view.topKpis.twelveFundingPct);
  const revSharePctLabel = Math.round(view.topKpis.twelveRevSharePct);
  const displayColumns = getClientCohortDisplayColumns(view.monthCount);
  const display = (values) => selectClientCohortDisplayValues(values, displayColumns);
  const displayTotals = computeClientCohortDisplayTotals(view, displayColumns);
  const displayEquity = computeClientCohortDisplayEquity(view, displayTotals);
  const lastDisplayedMonth = displayColumns.length
    ? displayColumns[displayColumns.length - 1].index
    : view.monthCount - 1;
  const supplierNote =
    view.marketingGrace === 1
      ? 'Month 0 is the UA month; supplier paid net-30.'
      : 'Month 0 is the UA month; supplier paid in cohort month.';

  return (
    <Box className="client-cohort-section card" mt={6}>
      <Flex
        className="section-header"
        justify="space-between"
        align="center"
        gap={3}
        flexWrap="wrap"
      >
        <Text fontWeight="semibold" fontSize="lg">
          Your Cash Flow with Twelve
        </Text>
        <DownloadExcelButton
          size="sm"
          label="Download PowerPoint"
          onDownload={async () => {
            try {
              await downloadClientCohortSlides({
                calcResult: dealIrrModel ? null : calcResult,
                dealIrrModel,
                dealIrrInputs,
                state,
                filenameBase: state?.modelName?.trim() || 'quick-cashflow',
              });
            } catch (err) {
              toast({
                title: 'Download failed',
                description: err?.message || 'Could not download the PowerPoint.',
                status: 'error',
              });
            }
          }}
        />
      </Flex>

      <Box p={5}>
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={6}>
          <TopKpiCard
            label="Cohort marketing spend"
            value={fmtMoney(view.topKpis.cohortMarketingSpend)}
          />
          <TopKpiCard
            label={`Twelve funding (${fundingPctLabel}%)`}
            value={fmtMoney(view.topKpis.twelveFunding)}
          />
          <TopKpiCard
            label="Capped return"
            value={fmtCappedReturnPct(view.topKpis.cappedReturnPct)}
          />
        </SimpleGrid>

        <Text fontSize="sm" fontWeight="semibold" mb={3}>
          How the cohort plays out for you, month by month
        </Text>

        <Box overflowX="auto" mb={4}>
          <table className="client-cohort-table">
            <thead>
              <tr>
                <th className="client-cohort-label-col" />
                {displayColumns.map((col) => (
                  <th key={col.header}>{col.header}</th>
                ))}
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              <ClientTableRow
                label="ROAS %"
                values={display(view.rows.roasPct)}
                total={displayTotals.roasPct}
                format="roas"
              />
              <ClientTableRow
                label="Cohort revenue"
                values={display(view.rows.cohortRevenue)}
                total={displayTotals.cohortRevenue}
              />
              <ClientTableRow
                label={`Twelve funds you (${fundingPctLabel}%)`}
                values={display(view.rows.twelveFundsYou)}
                total={displayTotals.twelveFundsYou}
              />
              <ClientTableRow
                label="Customer repays supplier (net 30)"
                values={display(view.rows.customerRepaysSupplier)}
                total={displayTotals.customerRepaysSupplier}
              />
              <ClientTableRow
                label={`Customer repays Twelve (${revSharePctLabel}%)`}
                values={display(view.rows.customerRepaysTwelve)}
                total={displayTotals.customerRepaysTwelve}
              />
              <ClientTableRow
                label="Customer net cash (month)"
                values={display(view.rows.customerNetCash)}
                total={displayTotals.customerNetCash}
                bold
              />
              <ClientTableRow
                label="Customer cash position (cum.)"
                values={display(view.rows.customerCashPosition)}
                total={displayTotals.customerCashPosition}
                highlight
                bold
              />
            </tbody>
          </table>
        </Box>

        <Text fontSize="xs" color="gray.500" mb={6}>
          Note: {supplierNote} Values in table are $ in 000s.
        </Text>

        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
          <BottomKpiCard
            label="Cost of financing"
            value={fmtMoney(view.bottomKpis.costOfFinancing)}
            sublabel={`${view.bottomKpis.costOfFinancingPct.toFixed(0)}% of funding`}
          />
          <BottomKpiCard
            label="Out of pocket on Day 1"
            value={fmtMoney(view.bottomKpis.outOfPocketDay1)}
            sublabel="at M1"
          />
          <Box className="client-cohort-equity-panel">
            <Text className="client-cohort-equity-label">
              RETURN ON YOUR EQUITY (at M{lastDisplayedMonth})
            </Text>
            <Flex align="center" justify="space-between" gap={4} mt={3}>
              <Box>
                <Text className="client-cohort-equity-sub">without Twelve</Text>
                <Text className="client-cohort-equity-without">
                  {fmtMultiple(displayEquity.equityWithoutTwelve)}
                </Text>
              </Box>
              <Text className="client-cohort-equity-arrow">→</Text>
              <Box textAlign="right">
                <Text className="client-cohort-equity-sub">with Twelve</Text>
                <Text className="client-cohort-equity-with">
                  {fmtMultiple(displayEquity.equityWithTwelve)}
                </Text>
              </Box>
            </Flex>
          </Box>
        </SimpleGrid>
      </Box>
    </Box>
  );
}

ClientCohortSection.propTypes = {
  calcResult: PropTypes.object,
  dealIrrModel: PropTypes.object,
  dealIrrInputs: PropTypes.object,
  state: PropTypes.object.isRequired,
};

export default ClientCohortSection;
